import React, { useState, useEffect } from 'react';
import { AlertTriangle, DollarSign, Lock } from 'lucide-react';
import { theme } from '../styles/theme';

interface CostConfirmationProps {
  costSGD: number;
  onConfirmationChange: (isConfirmed: boolean) => void;
  className?: string;
}

const COST_THRESHOLD = 5.0; // $5 SGD threshold
const CONFIRMATION_PHRASE = 'Paul Allowed';

export const CostConfirmation: React.FC<CostConfirmationProps> = ({
  costSGD,
  onConfirmationChange,
  className = '',
}) => {
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  
  const requiresDoubleConfirmation = costSGD >= COST_THRESHOLD;
  const isTextConfirmed = !requiresDoubleConfirmation || confirmationText.trim() === CONFIRMATION_PHRASE;
  const isFullyConfirmed = checkboxChecked && isTextConfirmed;

  // Notify parent of confirmation status changes
  useEffect(() => {
    onConfirmationChange(isFullyConfirmed);
  }, [isFullyConfirmed, onConfirmationChange]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Cost Tier Indicator */}
      <div className={`flex items-start gap-3 p-4 rounded-lg border-2 ${
        requiresDoubleConfirmation 
          ? 'bg-qc-error/10 border-qc-error' 
          : 'bg-qc-warning/10 border-qc-warning'
      }`}>
        {requiresDoubleConfirmation ? (
          <Lock className="w-5 h-5 text-qc-error flex-shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-qc-warning flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="font-semibold">
              {requiresDoubleConfirmation 
                ? 'High Cost Job - Double Confirmation Required' 
                : 'Cost Confirmation Required'}
            </span>
          </div>
          <p className="text-sm opacity-90">
            {requiresDoubleConfirmation 
              ? `This job costs SGD ${costSGD.toFixed(2)} (≥ $5.00). Additional confirmation required for high-cost jobs.`
              : `This job costs SGD ${costSGD.toFixed(2)}. Please confirm to proceed.`
            }
          </p>
        </div>
      </div>

      {/* Checkbox Confirmation */}
      <div className="flex items-start gap-3 p-4 bg-bg-surface rounded-lg border border-border">
        <input
          type="checkbox"
          id="cost-agreement-checkbox"
          checked={checkboxChecked}
          onChange={(e) => setCheckboxChecked(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
        />
        <label
          htmlFor="cost-agreement-checkbox"
          className={`text-sm ${theme.text.primary} cursor-pointer select-none`}
        >
          I understand and approve this estimated cost of{' '}
          <span className="font-semibold">SGD {costSGD.toFixed(2)}</span>.
          {requiresDoubleConfirmation && ' I acknowledge this is a high-cost job.'}
        </label>
      </div>

      {/* Text Input for High Cost Jobs */}
      {requiresDoubleConfirmation && (
        <div className="p-4 bg-bg-surface rounded-lg border-2 border-qc-error/50">
          <div className="mb-3">
            <label
              htmlFor="confirmation-phrase"
              className={`block text-sm font-medium ${theme.text.primary} mb-2`}
            >
              <span className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-qc-error" />
                Additional Confirmation Required
              </span>
            </label>
            <p className={`text-xs ${theme.text.secondary} mb-3`}>
              Type <span className="font-mono font-bold text-qc-error">"{CONFIRMATION_PHRASE}"</span> exactly to proceed with this high-cost job:
            </p>
            <input
              type="text"
              id="confirmation-phrase"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={`Type "${CONFIRMATION_PHRASE}" here`}
              className={`w-full px-4 py-3 bg-bg-base border-2 rounded-lg font-mono ${theme.text.primary} focus:outline-none focus:ring-2 ${
                confirmationText.trim() === CONFIRMATION_PHRASE
                  ? 'border-qc-success focus:ring-qc-success'
                  : confirmationText.length > 0
                  ? 'border-qc-error focus:ring-qc-error'
                  : 'border-border focus:ring-primary'
              }`}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {/* Feedback Messages */}
          {confirmationText.length > 0 && (
            <div className="mt-2">
              {confirmationText.trim() === CONFIRMATION_PHRASE ? (
                <p className="text-sm text-qc-success flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Confirmation phrase correct. You can now start the job.
                </p>
              ) : (
                <p className="text-sm text-qc-error flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Incorrect phrase. Must match exactly: "{CONFIRMATION_PHRASE}"
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Final Status Indicator */}
      <div className={`p-3 rounded-lg border ${
        isFullyConfirmed 
          ? 'bg-qc-success/10 border-qc-success' 
          : 'bg-bg-subtle border-border'
      }`}>
        <p className={`text-sm text-center ${
          isFullyConfirmed 
            ? 'text-qc-success font-medium' 
            : theme.text.tertiary
        }`}>
          {isFullyConfirmed ? (
            <> All confirmations complete. Ready to start job.</>
          ) : requiresDoubleConfirmation ? (
            <>Complete both confirmations above to proceed</>
          ) : (
            <>Check the box above to proceed</>
          )}
        </p>
      </div>
    </div>
  );
};
